/**
 * The supported-language set — a MIRROR of the one declaration in
 * `agents/shared/languages.json` (DECISIONS 2026-09-05 Q8, #1745).
 *
 * The agents (`agents/shared/languages.py`) and the backend validator
 * (`backend/src/config/coachCharacters.js`) read that file directly; the
 * browser bundle cannot, so this table copies it and
 * `languages.test.ts` fails the build the moment the two drift.
 *
 * `companion: true` — a family can choose it for the companion (voice and
 * text prompts exist). `companion: false` — text-only: a valid stored
 * preference used for staff↔family chat translation, never offered by the
 * companion-voice selector.
 */
export interface SupportedLanguage {
  code: LanguageCode;
  /** English display name ("Spanish"). */
  name: string;
  /** The language's own name ("Español"). */
  nativeName: string;
  companion: boolean;
  /**
   * BCP 47 tag (`pt-BR`, `zh-Hans`). The short `code` is the storage,
   * catalog-folder and URL key; this tag is the only value that goes to
   * `<html lang>`, `Intl.*` and `:lang()` (#2109 ruling B1).
   */
  bcp47: string;
}

export type LanguageCode = 'en' | 'es' | 'zh' | 'fil' | 'pt' | 'fr';

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = [
  { code: 'en', name: 'English', nativeName: 'English', companion: true, bcp47: 'en' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', companion: true, bcp47: 'es' },
  { code: 'zh', name: 'Mandarin', nativeName: '中文', companion: true, bcp47: 'zh-Hans' },
  { code: 'fil', name: 'Filipino', nativeName: 'Filipino', companion: false, bcp47: 'fil' },
  // pt and fr are text-only (#2109 ruling B2) and append after fil so index pins hold.
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', companion: false, bcp47: 'pt-BR' },
  { code: 'fr', name: 'French', nativeName: 'Français', companion: false, bcp47: 'fr' },
] as const;

/** Languages the companion-voice selector offers. */
export const COMPANION_LANGUAGES: readonly SupportedLanguage[] = SUPPORTED_LANGUAGES.filter((l) => l.companion);

export function isSupportedLanguage(code: unknown): code is LanguageCode {
  return typeof code === 'string' && SUPPORTED_LANGUAGES.some((l) => l.code === code);
}

/** English display name for a code; the code itself when unknown. */
export function languageDisplayName(code: string | undefined | null): string {
  if (!code) return '';
  return SUPPORTED_LANGUAGES.find((l) => l.code === code)?.name ?? code;
}

/** Selector label: the native name, with the English name when they differ ("中文 (Mandarin)"). */
export function languageSelectorLabel(lang: SupportedLanguage): string {
  return lang.nativeName === lang.name ? lang.name : `${lang.nativeName} (${lang.name})`;
}
